/**
 * The standing conditions of the document that decide whether an enforced style can be set here at
 * all, or would have no effect once it were.
 *
 * These read the document rather than the operator's draft, so they say the same thing before and
 * after any edit made in this control.
 */
import { t } from "../../../i18n";

interface Props {
  locked: boolean;
  /** The document's master switch, or `undefined` when it holds no value this control understands. */
  centralImagesEnabled: boolean | undefined;
  /** Whether any style in the document has a url template, without which there is nothing to enforce. */
  hasStyles: boolean;
  /** Whether the document text is not JSON, in which case nothing else here can be read from it. */
  documentUnreadable: boolean;
}

export function DocumentNotices({ locked, centralImagesEnabled, hasStyles, documentUnreadable }: Props): JSX.Element {
  return (
    <>
      {documentUnreadable && (
        <div
          className="alert alert-warning"
          role="alert"
          data-testid="enforced-style-unreadable"
        >
          {t(
            "enforced-thumbnail-style.unreadable-document",
            undefined,
            "This document is not valid JSON, so the style it enforces cannot be read and there is nothing here to apply to it. Correct the json in the editor below to change the enforced style.",
          )}
        </div>
      )}

      {locked && (
        <div
          className="alert alert-warning"
          role="alert"
          data-testid="enforced-style-locked"
        >
          {t("enforced-thumbnail-style.locked", undefined, "This document is locked, so the enforced style cannot be changed here.")}
        </div>
      )}

      {centralImagesEnabled === false && (
        <div
          className="alert alert-warning"
          role="alert"
          data-testid="enforced-style-upstream-gate"
        >
          {t(
            "enforced-thumbnail-style.upstream-disabled",
            undefined,
            "Central images are switched off in this document (enabled is false), so the style saved here has no effect until they are switched back on.",
          )}
        </div>
      )}

      {!hasStyles && !documentUnreadable && (
        <div
          className="alert alert-info"
          role="alert"
          data-testid="enforced-style-no-styles"
        >
          {t(
            "enforced-thumbnail-style.no-styles",
            undefined,
            "This document has no image url templates under game.styles, so there is no style to enforce yet.",
          )}
        </div>
      )}
    </>
  );
}
